import { type QConfigTableData } from '@exam-management/common/types';
import { QConfigStructureCategory, type QConfigStatus } from '@exam-management/graphql';
import { TableBody } from '@shared/components';
import { randomUUID } from '@shared/utils';
import { useCallback, useState } from 'react';

import { ChapterTableRow, SubjectTableRow } from '../../components';
import { QuestionConditionsTableRow } from '../../containers';
import { useQConfigStructureModal } from '../../providers';

import { computeExpansionState } from './utils';

interface QConfigStructureTableBodyProps {
  rows: QConfigTableData[];
  status: QConfigStatus;
}

export default function QConfigStructureTableBody(props: Readonly<QConfigStructureTableBodyProps>) {
  const { rows, status } = props;
  // REMARK: Currently, data has a "Group" row, but it's not displayed in the table.
  // This allParentIds is used to set all parentIds to openIds, which will make all rows expanded, including the "Group" row.
  // This ensures that the children of the "Group" row are displayed correctly in the table.
  // Please be aware of changing allParentIds and the initial value of openIds.
  const allParentIds = new Set(rows.flatMap((row) => row.parentIds));
  const [openIds, setOpenIds] = useState<Set<string>>(allParentIds);
  const { setAction } = useQConfigStructureModal();
  const expandItem = useCallback((id: string) => {
    if (!openIds.has(id)) {
      setOpenIds((prev) => {
        return new Set([...prev, id]);
      });
    }
  }, []);

  const toggleItem = useCallback((id: string) => {
    setOpenIds((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(id)) {
        updatedSet.delete(id);
      } else {
        updatedSet.add(id);
      }
      return updatedSet;
    });
  }, []);

  return (
    <TableBody>
      {rows.map((row, rowIndex) => {
        const { id, parentIds, category } = row;
        const { isParentExpanded, isExpanded } = computeExpansionState(id, openIds, parentIds);

        if (!isParentExpanded) {
          return null;
        }

        switch (category) {
          case QConfigStructureCategory.Subject:
            return (
              <SubjectTableRow
                key={id}
                data={row}
                expanded={isExpanded}
                onExpand={toggleItem}
                status={status}
              />
            );
          case QConfigStructureCategory.Chapter: {
            const isLastChild = rows[rowIndex + 1]?.category !== QConfigStructureCategory.Chapter;

            return (
              <ChapterTableRow
                key={id}
                data={row}
                expanded={isExpanded}
                onExpand={expandItem}
                onToggle={toggleItem}
                sx={{
                  // REMARK: Trust me bro
                  '> td > div > div:first-of-type': {
                    minHeight: isLastChild ? '100%' : 'calc(100% + 1px)',
                  },
                }}
                status={status}
              />
            );
          }
          case QConfigStructureCategory.Single:
          case QConfigStructureCategory.ItemSet: {
            const uuid = randomUUID();

            const childrenData = rows.filter((item) => item.parentIds.includes(row.id));

            const childAndSelfRows = rows.filter(
              (item) => item.parentIds.includes(row.id) || item.id === row.id,
            );

            const handleOpenUpdateModal = () => {
              if (row.category === QConfigStructureCategory.Single) {
                setAction({
                  action: 'UPDATE_SINGLE_QUESTION_CONDITIONS',
                  payload: {
                    data: row,
                    rows: childAndSelfRows,
                  },
                });
              } else if (row.category === QConfigStructureCategory.ItemSet) {
                setAction({
                  action: 'UPDATE_ITEM_SET_QUESTION_CONDITIONS',
                  payload: {
                    data: row,
                  },
                });
              }
            };

            return (
              <QuestionConditionsTableRow
                key={`${uuid}-${id}`}
                rowData={row}
                childrenData={childrenData}
                status={status}
                onUpdate={handleOpenUpdateModal}
              />
            );
          }
          default:
            return null;
        }
      })}
    </TableBody>
  );
}
